import { currentAuthRole, isAdminRole, isBusinessContourRole } from './auth-roles';

/** Запись скоупа: набор прав, действующих в рамках одного бизнеса. */
export interface BusinessScopePermissions {
  business_id: string;
  permissions?: string[];
}

/** Эффективные права из `/auth/me` (BE-800). */
export interface EffectivePermissions {
  global?: string[];
  businesses?: BusinessScopePermissions[];
}

/** Состояние авторизации в объёме, нужном для проверки прав. */
export interface PermissionSource {
  currentUser?: { role?: string; permissions?: EffectivePermissions } | null;
  tokens?: { role?: string } | null;
}

/**
 * Ключи каталога прав (BE-800/BE-801). Каталог живёт на бэкенде и расширяется данными;
 * здесь — только те ключи, которыми гейтит фронт, чтобы не рассыпать строки по экранам.
 */
export const PERMISSION = {
  businessesView: 'businesses.view',
  businessesCreate: 'businesses.create',
  businessesEdit: 'businesses.edit',
  businessesStatusManage: 'businesses.status.manage',
  businessesStaffInvite: 'businesses.staff.invite',
  businessesAssignSelf: 'businesses.assign_self',
  businessUpdatesView: 'business_updates.view',
  businessUpdatesModerate: 'business_updates.moderate',
  shopsView: 'shops.view',
  shopsCreate: 'shops.create',
  shopsEdit: 'shops.edit',
  shopsStatusManage: 'shops.status.manage',
  promotionsView: 'promotions.view',
  promotionsCreate: 'promotions.create',
  promotionsEdit: 'promotions.edit',
  promotionsDelete: 'promotions.delete',
  promotionsModerate: 'promotions.moderate',
  promotionsLifecycle: 'promotions.lifecycle',
  applicationsView: 'applications.view',
  applicationsRegenerate: 'applications.regenerate',
  promocodesView: 'promocodes.view',
  reportsView: 'reports.view',
  reportsReview: 'reports.review',
  ordersCreate: 'orders.create',
  ordersImport: 'orders.import',
  auditView: 'audit.view',
  accountingInvoicesView: 'accounting.invoices.view',
  accountingInvoicesManage: 'accounting.invoices.manage',
} as const;

export type PermissionKey = (typeof PERMISSION)[keyof typeof PERMISSION];

/**
 * Ключи каталога бизнес-кабинета (BE-803/BE-807). Отдельный реестр, а не продолжение
 * PERMISSION: каталоги разные и не пересекаются даже по смыслу — `shops.view` админки
 * пускает менеджера в чужие магазины, `cabinet.shops.view` — сотрудника в свои. На бэке
 * они и разведены префиксом `cabinet.` ровно по этой причине.
 *
 * В отличие от PERMISSION здесь перечислен ВЕСЬ каталог бэкенда, а не только то, чем
 * гейтит фронт: список сверяется с ним тестом, и расхождение должно быть видно сразу.
 * Поэтому в нём есть ключи, которые сегодня ничего не закрывают — `marketplace.view` и
 * `marketplace.edit` стоят за ручками `/businesses/{id}/marketplace/*`, которых у
 * кабинета нет вовсе (раздел «Магазины» работает по `shops.*`).
 *
 * Все ключи кабинета business-scope: глобальных прав у сотрудника нет по построению.
 */
export const CABINET_PERMISSION = {
  profileView: 'cabinet.profile.view',
  profileEdit: 'cabinet.profile.edit',
  marketplaceView: 'cabinet.marketplace.view',
  marketplaceEdit: 'cabinet.marketplace.edit',
  shopsView: 'cabinet.shops.view',
  shopsEdit: 'cabinet.shops.edit',
  promotionsView: 'cabinet.promotions.view',
  promotionsEdit: 'cabinet.promotions.edit',
  promotionsSign: 'cabinet.promotions.sign',
  promotionsLifecycle: 'cabinet.promotions.lifecycle',
  scannerScan: 'cabinet.scanner.scan',
  scannerHistory: 'cabinet.scanner.history',
  invoicesView: 'cabinet.invoices.view',
  invoicesRequest: 'cabinet.invoices.request',
  ordersView: 'cabinet.orders.view',
  ordersRefund: 'cabinet.orders.refund',
  applicationsView: 'cabinet.applications.view',
  reportsView: 'cabinet.reports.view',
  reportsCreate: 'cabinet.reports.create',
  staffView: 'cabinet.staff.view',
  integrationsView: 'cabinet.integrations.view',
  integrationsManage: 'cabinet.integrations.manage',
} as const;

export type CabinetPermissionKey = (typeof CABINET_PERMISSION)[keyof typeof CABINET_PERMISSION];

/**
 * Права каталога, которые не показываем владельцу при выдаче (BE-807).
 *
 * `marketplace.*` — легаси: за ними стоят только ручки `/businesses/{id}/marketplace/*`,
 * и все шесть помечены на бэкенде `deprecated` (BE-794 — ведение витрины перенесено на
 * уровень магазина, `ShopProfileUpdate`). Ни одного экрана и ни одной кнопки за ними в
 * кабинете нет: то, что покупатель видит витриной, правится правами `shops.*`. Тумблер,
 * который ничего не меняет, владельца только путает — «выдал доступ к витрине, а
 * сотрудник её не видит».
 *
 * Скрываем ТОЛЬКО отображение. Если такое право уже выдано (оно входит в пресеты
 * «Ведение магазина» и «Полный доступ»), при сохранении оно остаётся в наборе: PUT
 * заменяет права целиком, и молча снять невидимое хуже, чем не показать.
 */
export const HIDDEN_CABINET_PERMISSIONS: ReadonlySet<string> = new Set<string>([
  CABINET_PERMISSION.marketplaceView,
  CABINET_PERMISSION.marketplaceEdit,
]);

/**
 * Права, действующие по всей платформе, а не в рамках бизнеса. Остальные ключи каталога —
 * business-scope.
 *
 * Зачем список здесь, а не из каталога: `/admin/permissions/catalog` доступен только
 * админу и root, а гейтить нужно как раз менеджера и бухгалтера — спросить у бэкенда
 * scope своих прав они не могут. Расхождение с каталогом не опасно в одну сторону:
 * неизвестный фронту global-ключ будет вести себя как business-scope (см. hasPermission).
 */
const GLOBAL_SCOPE_KEYS: ReadonlySet<string> = new Set<string>([
  PERMISSION.businessesCreate,
  PERMISSION.businessesAssignSelf,
  PERMISSION.auditView,
  PERMISSION.accountingInvoicesView,
  PERMISSION.accountingInvoicesManage,
]);

/**
 * Гейтим ли этого пользователя вообще.
 *
 * - админ и root проходят проверки бэкенда в обход грантов;
 * - бизнес-контур живёт на собственных ключах `cabinet.*` и спрашивает про них
 *   `hasCabinetPermission` (BE-807). Ключи админского каталога ему не выдаются вовсе,
 *   и гейтинг по их отсутствию закрыл бы кабинет целиком;
 * - профиль ещё не загружен (гварды и меню работают раньше ответа `/auth/me`) — «прав
 *   не знаем», а не «прав нет».
 */
function isGated(state: PermissionSource): boolean {
  const role = currentAuthRole(state);
  if (isAdminRole(role) || isBusinessContourRole(role)) {
    return false;
  }
  return state.currentUser?.permissions != null;
}

function globalKeys(state: PermissionSource): string[] {
  return state.currentUser?.permissions?.global ?? [];
}

function scopeEntries(state: PermissionSource): BusinessScopePermissions[] {
  return state.currentUser?.permissions?.businesses ?? [];
}

/**
 * Есть ли право вообще — для пунктов меню и гвардов разделов.
 *
 * Business-права приходят по записи на каждый бизнес в скоупе. Пустой скоуп не значит
 * «прав нет»: у менеджера без закреплённых бизнесов массив пуст, но списочные ручки
 * работают и отдают пустой список — раздел в этом случае оставляем открытым.
 */
export function hasPermission(state: PermissionSource, key: string): boolean {
  if (!isGated(state)) {
    return true;
  }
  if (globalKeys(state).includes(key)) {
    return true;
  }
  if (GLOBAL_SCOPE_KEYS.has(key)) {
    // Global-права приходят списком целиком: нет в нём — значит не выдано.
    return false;
  }
  const entries = scopeEntries(state);
  if (entries.length === 0) {
    return true;
  }
  return entries.some((entry) => (entry.permissions ?? []).includes(key));
}

/**
 * Есть ли право по конкретному бизнесу — для действий в его карточке. Бизнес вне скоупа
 * недоступен всегда: менеджер видит в реестре и незакреплённые бизнесы, но действовать
 * по ним не может.
 */
export function hasBusinessPermission(
  state: PermissionSource,
  key: string,
  businessId: string | null,
): boolean {
  if (!isGated(state)) {
    return true;
  }
  if (globalKeys(state).includes(key)) {
    return true;
  }
  return businessKeys(state, businessId).includes(key);
}

/**
 * Набор прав, выданный в этом бизнесе. Пустой — и когда записи нет, и когда она пуста, и
 * пока профиль не загружен.
 */
export function businessKeys(state: PermissionSource, businessId: string | null | undefined): string[] {
  if (!businessId) {
    return [];
  }
  return scopeEntries(state).find((item) => item.business_id === businessId)?.permissions ?? [];
}

/** Бизнесы в скоупе пользователя. */
export function businessScopeIds(state: PermissionSource): string[] {
  return scopeEntries(state).map((entry) => entry.business_id);
}

/**
 * Есть ли право в бизнес-кабинете (BE-807). Спрашивается всегда в паре с `businessId`:
 * в кабинете грант привязан к конкретному membership, и сотрудник двух юрлиц иначе
 * получил бы в обоих то, что выдано в одном.
 *
 * Почему не `hasBusinessPermission`: там отсутствие записи о бизнесе разбирается по
 * правилам админского контура, где пустой скоуп значит «прав не знаем» (менеджер без
 * закреплённых бизнесов). В кабинете это неверно — бэкенд не присылает запись, пока в
 * ней нет ни одного гранта, поэтому сотрудник без прав приходит с пустым `businesses`,
 * и трактовка «не знаем» открыла бы ему весь кабинет под 403 на каждой ручке.
 *
 * Не гейтим в двух случаях: админ и root проходят проверки бэкенда в обход грантов, а
 * пока профиль не загружен (меню и гварды работают раньше ответа `/auth/me`) «прав не
 * знаем» — иначе владельца выбрасывало бы из кабинета по прямой ссылке.
 */
export function hasCabinetPermission(
  state: PermissionSource,
  key: string,
  businessId: string | null | undefined,
): boolean {
  if (isAdminRole(currentAuthRole(state))) {
    return true;
  }
  if (state.currentUser?.permissions == null) {
    return true;
  }
  return businessKeys(state, businessId).includes(key);
}
